import {
  wishlistManagementModalActions,
  WishlistManagementModal as WishlistManagementModalComponent,
  WishlistManagementModalState,
  wishlistSnackbarActions,
  WishlistSnackbarType,
} from "halifax";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  FAVORITES_ADD_ITEM,
  FAVORITES_CREATE_LIST,
  FAVORITES_EDITED_LIST,
  FAVORITES_VIEWED_MODAL,
  FavoritesEntryProperties,
  FavoritesItemProperties,
  FavoritesListProperties,
} from "redmond";
import { History } from "history";
import { StayType } from "redmond/hotels-module/interfaces";
import { LodgingCollectionEnum } from "redmond";
import dayjs from "dayjs";

const stayTypeMap: Partial<Record<StayType, "hotel" | "vr">> = {
  [StayType.Hotels]: "hotel",
  [StayType.Homes]: "vr",
};

const collectionTypePrefixMap: Record<LodgingCollectionEnum, string> = {
  [LodgingCollectionEnum.Premier]: "premier_",
  [LodgingCollectionEnum.Lifestyle]: "lifestyle_",
  [LodgingCollectionEnum.NoCollection]: "",
};

export interface WishlistManagementModalProps {
  history: History;
  fromDate: Date | null;
  untilDate: Date | null;
  adultsCount: number;
  childrenCount: number[];
  searchedPetsCount: number;
  mobileModalFullscreen?: boolean;
  entryType?: string;
}

export const WishlistManagementModal = ({
  history,
  fromDate,
  untilDate,
  adultsCount,
  childrenCount,
  searchedPetsCount,
  mobileModalFullscreen,
  entryType,
}: WishlistManagementModalProps) => {
  const dispatch = useDispatch();

  const { isOpen, listingId, listingImageUrl, stayType, lodgingCollection } =
    useSelector(
      (state: { wishlistManagementModal: WishlistManagementModalState }) =>
        state.wishlistManagementModal
    );

  useEffect(() => {
    if (isOpen && entryType) {
      trackEvent({
        eventName: FAVORITES_VIEWED_MODAL,
        properties: {
          entry_type: entryType,
        } as FavoritesEntryProperties,
      });
    }
  }, [isOpen, entryType]);

  const onClose = () => {
    dispatch(wishlistManagementModalActions.closeModal());
  };

  const handleOnCreateWishlist = (wishlistId: string) => {
    trackEvent({
      eventName: FAVORITES_CREATE_LIST,
      properties: {
        favorites_id: wishlistId,
      } as FavoritesListProperties,
    });
  };

  const handleOnAddToWishlist = ({ wishlistId }: { wishlistId: string }) => {
    dispatch(
      wishlistSnackbarActions.showSnackbar({
        type: WishlistSnackbarType.ADD,
        wishlistId: wishlistId,
        listingId: listingId,
        listingImageUrl: listingImageUrl,
      })
    );

    const baseItemType = stayType ? stayTypeMap[stayType] : "";
    const prefix = lodgingCollection
      ? collectionTypePrefixMap[lodgingCollection]
      : "";
    const itemType = `${prefix}${baseItemType}`;

    trackEvent({
      eventName: FAVORITES_ADD_ITEM,
      properties: {
        item_id: listingId,
        item_type: itemType,
        favorites_id: wishlistId,
      } as FavoritesItemProperties & FavoritesListProperties,
    });
    onClose();
  };

  const handleOnDeleteWishlist = (wishlistId: string) => {
    trackEvent({
      eventName: FAVORITES_EDITED_LIST,
      properties: {
        favorites_id: wishlistId,
        action: "delete",
      } as FavoritesListProperties,
    });
  };

  if (!fromDate || !untilDate) {
    const today = dayjs().startOf("day");

    fromDate = today.add(2, "day").toDate();
    untilDate = today.add(6, "day").toDate();
  }

  return (
    <WishlistManagementModalComponent
      isOpen={isOpen}
      mobileModalFullscreen={mobileModalFullscreen}
      history={history}
      selectedListingId={listingId}
      stayType={stayType}
      onAddToWishlist={handleOnAddToWishlist}
      onCreateWishlist={handleOnCreateWishlist}
      onDeleteWishlist={handleOnDeleteWishlist}
      onViewWishlist={onClose}
      onClose={onClose}
      stayDates={{ fromDate, untilDate }}
      guests={{
        adults: adultsCount,
        children: childrenCount,
        pets: searchedPetsCount,
      }}
    />
  );
};
